import { createContext, useContext, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router-dom";
import { apiGet, apiPost, ApiError } from "./api";

export interface TenantOption {
  id: string;
  display_name: string;
}

export interface CurrentUser {
  id: number;
  email: string;
  full_name: string | null;
  role: string;
  // The *effective* tenant. Equals home_tenant_id unless the user is a
  // platform admin who has switched. All API endpoints filter on this.
  tenant_id: string;
  home_tenant_id: string;
  // Tenants this user is allowed to switch between. Single-entry list
  // for tenant-scoped users; full active list for platform admins.
  // The topbar shows a dropdown only when this has more than one entry.
  visible_tenants: TenantOption[];
}

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue>({ user: null, isLoading: true });

export function AuthProvider({ children }: { children: ReactNode }) {
  // /auth/me returns 401 if there's no session — that's the "not logged in"
  // signal. Keep the user cached for 5 min; we revalidate on focus/network
  // recovery via TanStack Query defaults so a tab left open across a logout
  // gets bounced to /login on its next interaction.
  const { data, isLoading } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: ({ signal }) => apiGet<CurrentUser>("/auth/me", signal),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) return false;
      return failureCount < 1;
    },
    staleTime: 5 * 60 * 1000,
  });

  const user = (data ?? null) as CurrentUser | null;
  return <AuthContext.Provider value={{ user, isLoading }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      apiPost<CurrentUser>("/auth/login", body),
    onSuccess: (user) => {
      qc.setQueryData(["auth", "me"], user);
    },
  });
}

export function useSwitchTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenant_id: string | null) =>
      apiPost<CurrentUser>("/auth/switch-tenant", { tenant_id }),
    onSuccess: (user) => {
      // Two-step: write the new user immediately (so the topbar updates
      // without a flash of stale state), then nuke every other cached
      // query — every list view is tenant-scoped, no point keeping rows
      // from the previous tenant around. /auth/me stays via the explicit
      // setQueryData below.
      qc.clear();
      qc.setQueryData(["auth", "me"], user);
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { current_password: string; new_password: string }) =>
      apiPost<{ ok: string }>("/auth/change-password", body),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ ok: string }>("/auth/logout", {}),
    onSettled: () => {
      qc.setQueryData(["auth", "me"], null);
      qc.removeQueries({ queryKey: ["auth", "me"] });
      // Hard reset: the panel relies on no-stale-data after logout.
      qc.clear();
    },
  });
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return <>{children}</>;
}
